import { useState, useEffect, useMemo, useCallback } from "react";
import { auth, db } from "../firebase/config";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { DateTime } from "luxon";
import "../styles/TrainingTracker.css";

export default function TrainingTracker() {
  const [companyId, setCompanyId] = useState(null);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [trainingProgress, setTrainingProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UI controls
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | completed | overdue | progress
  const [sortBy, setSortBy] = useState("deadlineAsc"); // deadlineAsc | deadlineDesc | title

  /* ===== Load company id + timezone ===== */
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) return setLoading(false);
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) throw new Error("User doc not found.");

        const cid = userSnap.data()?.companyId;
        if (!cid) throw new Error("Company ID not found.");
        setCompanyId(cid);

        const companySnap = await getDoc(doc(db, "companies", cid));
        const tz = companySnap.data()?.timeZone || "UTC";
        setCompanyTimeZone(tz);
      } catch (e) {
        setError(e?.message || "Failed to load company settings.");
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  /* ===== Employees ===== */
  useEffect(() => {
    if (!companyId) return;
    const run = async () => {
      try {
        setLoading(true);
        const snap = await getDocs(
          collection(db, `companies/${companyId}/employees`)
        );
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEmployees(list);
      } catch {
        setError("Failed to fetch employees.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [companyId]);

  /* ===== Progress for selected employee ===== */
  useEffect(() => {
    if (!companyId || !selectedEmployee) return;
    const run = async () => {
      try {
        setLoading(true);
        const progressSnap = await getDocs(
          collection(
            db,
            `companies/${companyId}/employees/${selectedEmployee}/training_progress`
          )
        );
        const assigned = progressSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // join with course metadata
        const joined = await Promise.all(
          assigned.map(async (row) => {
            if (!row.courseId) return row;

            const courseSnap = await getDoc(
              doc(db, `companies/${companyId}/training_courses`, row.courseId)
            );

            if (!courseSnap.exists()) return row;

            const courseData = courseSnap.data() || {};

            // ✅ IMPORTANT: keep deadline from training_progress (assignment deadline)
            // and store it in a stable field name used everywhere in the tracker.
            return {
              ...courseData, // course title, description, type, contentUrl, etc.
              ...row, // progress fields: status, assignedAt, etc.
              assignmentDeadline: row.deadline || null, // ✅ source of truth
            };
          })
        );

        setTrainingProgress(joined.filter(Boolean));
      } catch {
        setError("Failed to fetch training progress.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [companyId, selectedEmployee]);

  /* ===== Helpers ===== */
  const toISO = useCallback((d) => {
    return d?.toDate
      ? d.toDate().toISOString()
      : typeof d === "string"
      ? d
      : null;
  }, []);

  const isOverdue = useCallback(
    (course) => {
      if (course.status === "completed" || !course.assignmentDeadline)
        return false;

      const iso = toISO(course.assignmentDeadline);
      if (!iso) return false;

      const due = DateTime.fromISO(iso, { zone: companyTimeZone });
      return due < DateTime.now().setZone(companyTimeZone);
    },
    [companyTimeZone, toISO]
  );

  const daysUntil = useCallback(
    (course) => {
      const iso = toISO(course.assignmentDeadline);
      if (!iso) return null;

      const due = DateTime.fromISO(iso, { zone: companyTimeZone });
      return Math.ceil(
        due.diff(DateTime.now().setZone(companyTimeZone), "days").days
      );
    },
    [companyTimeZone, toISO]
  );

  /* ===== Derived rows (filter/sort + formatting) ===== */
  const rows = useMemo(() => {
    let list = trainingProgress.map((c) => ({
      ...c,
      _overdue: isOverdue(c),
      _daysUntil: daysUntil(c),
      _deadlineFmt: c.assignmentDeadline
        ? DateTime.fromISO(toISO(c.assignmentDeadline), {
            zone: companyTimeZone,
          }).toFormat("ccc, dd LLL yyyy")
        : "No deadline",
    }));

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (c) =>
          (c.courseTitle || "").toLowerCase().includes(q) ||
          (c.type || "").toLowerCase().includes(q) ||
          (c.status || "").toLowerCase().includes(q)
      );
    }

    if (statusFilter === "completed")
      list = list.filter((c) => c.status === "completed");
    if (statusFilter === "overdue") list = list.filter((c) => c._overdue);
    if (statusFilter === "progress")
      list = list.filter((c) => c.status !== "completed" && !c._overdue);

    if (sortBy === "title")
      list.sort((a, b) =>
        (a.courseTitle || "").localeCompare(b.courseTitle || "")
      );
    if (sortBy === "deadlineAsc")
      list.sort((a, b) =>
        (toISO(a.assignmentDeadline) || "").localeCompare(
          toISO(b.assignmentDeadline) || ""
        )
      );

    if (sortBy === "deadlineDesc")
      list.sort((a, b) =>
        (toISO(b.assignmentDeadline) || "").localeCompare(
          toISO(a.assignmentDeadline) || ""
        )
      );
    return list;
  }, [
    trainingProgress,
    query,
    statusFilter,
    sortBy,
    companyTimeZone,
    isOverdue,
    daysUntil,
    toISO,
  ]);

  /* ===== KPIs ===== */
  const kpis = useMemo(() => {
    const total = trainingProgress.length;
    const completed = trainingProgress.filter((c) => c.status === "completed")
      .length;
    const overdue = trainingProgress.filter((c) => isOverdue(c)).length;
    const inProgress = total - completed - overdue;
    return { total, completed, overdue, inProgress };
  }, [trainingProgress, isOverdue]);

  /* ===== Actions ===== */
  const renderAction = (course) => {
    if (!course.contentUrl) return null;
    const label =
      course.type === "video"
        ? "Watch Video"
        : course.type === "pdf"
        ? "View PDF"
        : "Open Link";
    return (
      <a
        className="tracker-link"
        href={course.contentUrl}
        target="_blank"
        rel="noreferrer"
      >
        {label}
      </a>
    );
  };

  return (
    <div className="tracker-wrap">
      <div className="tracker-header"></div>

      {loading && (
        <div className="tracker-skeleton">Loading training data…</div>
      )}
      {error && <div className="tracker-error">{error}</div>}

      {!loading && !error && (
        <div className="tracker-card">
          {/* Toolbar */}
          <div className="tracker-toolbar">
            <div className="toolbar-row">
              <div className="toolbar-field">
                <label>Employee</label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                >
                  <option value="">Select Employee</option>
                  {employees
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="toolbar-field">
                <label>Search</label>
                <input
                  type="text"
                  placeholder="Search title, type, status…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              <div className="toolbar-field">
                <label>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="progress">In Progress</option>
                  <option value="overdue">Overdue</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="toolbar-field">
                <label>Sort</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="deadlineAsc">Deadline ↑</option>
                  <option value="deadlineDesc">Deadline ↓</option>
                  <option value="title">Title A–Z</option>
                </select>
              </div>
            </div>

            {/* KPIs */}
            {selectedEmployee && (
              <div className="tracker-kpis">
                <div className="kpi">
                  <span className="kpi-label">Assigned</span>
                  <span className="kpi-value">{kpis.total}</span>
                </div>
                <div className="kpi">
                  <span className="kpi-label">Completed</span>
                  <span className="kpi-value kpi-ok">{kpis.completed}</span>
                </div>
                <div className="kpi">
                  <span className="kpi-label">In Progress</span>
                  <span className="kpi-value kpi-warn">{kpis.inProgress}</span>
                </div>
                <div className="kpi">
                  <span className="kpi-label">Overdue</span>
                  <span className="kpi-value kpi-bad">{kpis.overdue}</span>
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          {selectedEmployee ? (
            rows.length === 0 ? (
              <div className="tracker-empty">
                No training found for your filters. Clear search or pick another
                status.
              </div>
            ) : (
              <div className="tracker-table-wrap">
                <table className="tracker-table">
                  <thead>
                    <tr>
                      <th>Course Title</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Deadline</th>
                      <th>Due In</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((course) => (
                      <tr key={course.id}>
                        <td className="title-cell">
                          <div className="title-main">{course.courseTitle}</div>
                          {course.description ? (
                            <div className="title-sub">
                              {course.description}
                            </div>
                          ) : null}
                        </td>

                        <td className="type-cell">
                          {course.type === "video"
                            ? "Video"
                            : course.type === "pdf"
                            ? "PDF"
                            : "Link"}
                        </td>

                        <td>
                          <span
                            className={[
                              "pill",
                              course.status === "completed"
                                ? "pill-ok" // green
                                : course._overdue
                                ? "pill-bad" // red
                                : "pill-warn", // orange
                            ].join(" ")}
                          >
                            {course.status === "completed"
                              ? "Completed"
                              : course._overdue
                              ? "Overdue"
                              : "In Progress"}
                          </span>
                        </td>

                        <td>{course._deadlineFmt}</td>

                        <td>
                          {course._daysUntil == null ? (
                            "—"
                          ) : (
                            <span
                              className={
                                course._daysUntil < 0
                                  ? "due-late" // red text
                                  : course._daysUntil <= 3
                                  ? "due-soon" // orange text
                                  : "due-ok" // green text
                              }
                            >
                              {course._daysUntil < 0
                                ? `${Math.abs(course._daysUntil)}d late`
                                : `${course._daysUntil}d`}
                            </span>
                          )}
                        </td>

                        <td>{renderAction(course)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className="tracker-empty">
              Select an employee to view their training.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
